import { useEffect, useRef } from 'react'

import { FastlaneTrackingEvents } from 'constants/fastlane'
import { trackEvent } from 'utils/tracking'

import { FLWizardStep } from './FLCreationWizard'

/**
 * Triggers the `CancelFastlaneCreationWizard` segment event when the user cancels/leaves the wizard
 *
 * @description
 * Uses a combo of `useRef` and `useEffect` to create a single `history.listen` listener
 * which handles sending a "cancel" or "leave" segment event only once, while passing
 * the most up to date `activeStep` & `step.trackingId` value
 */
export const useTrackWizardCancelEvent = ({
  activeStep,
  steps,
  history,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  history: any
  activeStep: number
  steps: FLWizardStep[]
}) => {
  // We need to track the steps, activeStep in a ref to make them available to
  // the useEffect without passing them as a dependency to the useEffect
  const trackingRef = useRef<{ steps: FLWizardStep[]; activeStep: number }>({
    steps: [],
    activeStep: 0,
  })

  // Update the values of the ref on every rerender (the ref itself never changes)
  trackingRef.current = {
    activeStep,
    steps,
  }

  useEffect(() => {
    const removeListener = history.listen((action: { pathname: string }) => {
      // After playbook creation we redirect to the /editor/:playbookId route
      const newPathIsEditor = action.pathname.startsWith('/editor/')

      // Only track if we're leaving the wizard without creating a playbook
      if (!newPathIsEditor) {
        trackEvent(FastlaneTrackingEvents.CancelFastlaneWizard, {
          stepNumber: trackingRef.current.activeStep + 1,
          step: trackingRef.current.steps[trackingRef.current.activeStep]
            .trackingId,
        })
      }
      removeListener()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
